import { api, get, post } from '@/lib/api';
import type {
  Calendario,
  Congelamento,
  ComponenteDeServico,
  Enforcement,
  ExcecaoDeCongelamento,
  IcAfetado,
  ImpactoNoIc,
  JanelaDeMudanca,
  JanelaDetalhada,
  JanelaListada,
  Mudanca,
  PreviaDeAgendamento,
  Provisionamento,
  QuestionarioDeRisco,
  ServicoAfetado,
  ServicoDoCatalogo,
  SituacaoDeJanela,
  DetalheDeMudanca,
  RespostaDeAvaliacao,
  TipoDeMudanca,
} from '@/features/changes/tipos';

/**
 * Acesso à API de Change Enablement.
 *
 * Uma função por rota que existe em `changes.controller.ts`, sem nenhuma
 * inventada. O que falta está **anotado**, não simulado:
 *
 *   • **não há rota que liste itens de configuração.** O CMDB só é alcançável
 *     por `GET /catalog/services/:id/components`, isto é, pelos ICs que são
 *     componentes de algum serviço do catálogo. `listarItensDeConfiguracao`
 *     abaixo monta o dicionário a partir dos serviços, e a tela diz que é isso
 *     que está oferecendo — em vez de fingir que enxerga o CMDB inteiro;
 *
 *   • **não há rota que liste mudanças com a janela de cada uma.**
 *     `GET /tickets?type=CHANGE` devolve chamados, sem `ChangeDetail`. Quem
 *     precisa do par mudança × janela lê `GET /changes/windows/:id`, que é o
 *     caminho que o calendário usa;
 *
 *   • **não há rota agregada de conflito.** A colisão é calculada por mudança,
 *     em `GET /tickets/:id/change`. O calendário a reúne no cliente — ver a
 *     nota em `CalendarioPage`.
 */

const MUDANCA = (ticketId: string) => `/tickets/${ticketId}/change`;
const CALENDARIO = '/changes';

const patch = <T>(caminho: string, corpo: unknown) =>
  api<T>(caminho, { method: 'PATCH', body: corpo });

function comIntervalo(caminho: string, de: Date, ate: Date): string {
  const busca = new URLSearchParams({ from: de.toISOString(), to: ate.toISOString() });
  return `${caminho}?${busca.toString()}`;
}

// ── a mudança ──────────────────────────────────────────────────────────────

export const obterMudanca = (ticketId: string) => get<Mudanca>(MUDANCA(ticketId));

export interface AlteracaoDeMudanca {
  changeType?: TipoDeMudanca;
  justification?: string;
  implementationPlan?: string;
  testPlan?: string;
  rollbackPlan?: string;
  implementationEvidence?: string;
  version?: number;
}

/**
 * `riskLevel` e `riskScore` não entram aqui, e não é esquecimento: o servidor
 * responde 400 a qualquer campo fora da lista branca, porque quem define o
 * nível é o gabarito do questionário. Duas fontes para o mesmo fato fariam a
 * política de aprovação usar a errada.
 */
export const alterarMudanca = (ticketId: string, corpo: AlteracaoDeMudanca) =>
  patch<DetalheDeMudanca>(MUDANCA(ticketId), corpo);

export const obterQuestionario = () =>
  get<QuestionarioDeRisco>(`${CALENDARIO}/risk-questionnaire`);

export const avaliarRisco = (
  ticketId: string,
  answers: Record<string, string>,
  version?: number,
) =>
  post<RespostaDeAvaliacao>(`${MUDANCA(ticketId)}/risk`, {
    answers,
    ...(version === undefined ? {} : { version }),
  });

export interface ServicoAfetadoEntrada {
  serviceId: string;
  causesOutage?: boolean;
  expectedDowntimeMinutes?: number | null;
  note?: string | null;
}

/** Substituição, e não acréscimo: a lista da tela **é** a lista. */
export const definirServicosAfetados = (
  ticketId: string,
  services: readonly ServicoAfetadoEntrada[],
) => post<ServicoAfetado[]>(`${MUDANCA(ticketId)}/services`, { services });

export interface IcAfetadoEntrada {
  ciId: string;
  impactKind?: ImpactoNoIc;
  causesOutage?: boolean;
  note?: string | null;
}

export const definirIcsAfetados = (ticketId: string, cis: readonly IcAfetadoEntrada[]) =>
  post<IcAfetado[]>(`${MUDANCA(ticketId)}/cis`, { cis });

/** O que colidiria nesta janela, **sem gravar nada**. */
export const preverAgendamento = (ticketId: string, windowId: string) =>
  get<PreviaDeAgendamento>(`${MUDANCA(ticketId)}/schedule/preview?windowId=${windowId}`);

/**
 * Agenda numa janela.
 *
 * 422 quando cai em congelamento bloqueante. Colisão com outra mudança **não**
 * impede: ela volta em `conflicts`, para a tela do Comitê de Mudanças.
 */
export const agendarMudanca = (ticketId: string, windowId: string, version?: number) =>
  post<{ change: DetalheDeMudanca; conflicts: Mudanca['conflicts'] }>(
    `${MUDANCA(ticketId)}/schedule`,
    { windowId, ...(version === undefined ? {} : { version }) },
  );

export const pedirExcecaoDeCongelamento = (
  ticketId: string,
  corpo: { blackoutPeriodId: string; justification: string; policyKey?: string },
) => post<ExcecaoDeCongelamento>(`${MUDANCA(ticketId)}/blackout-exception`, corpo);

export interface RevisaoPosImplementacao {
  outcome: 'SUCCESSFUL' | 'SUCCESSFUL_WITH_ISSUES' | 'FAILED' | 'ROLLED_BACK';
  pirNote: string;
  rollbackReason?: string;
  version?: number;
}

export const registrarRevisao = (ticketId: string, corpo: RevisaoPosImplementacao) =>
  post<DetalheDeMudanca>(`${MUDANCA(ticketId)}/pir`, corpo);

// ── calendário ─────────────────────────────────────────────────────────────

export const obterCalendario = (de: Date, ate: Date) =>
  get<Calendario>(comIntervalo(`${CALENDARIO}/schedule`, de, ate));

export const listarJanelas = () => get<JanelaListada[]>(`${CALENDARIO}/windows`);

export const obterJanela = (windowId: string) =>
  get<JanelaDetalhada>(`${CALENDARIO}/windows/${windowId}`);

export interface NovaJanela {
  name: string;
  description?: string;
  startsAt: string;
  endsAt: string;
  ownerTeamId?: string;
  recurrenceNote?: string;
}

/**
 * A resposta é a **linha crua**, sem `_count`: o `create` do servidor não pede
 * a contagem, e uma janela recém-criada não tem nada dentro mesmo. Tipar como
 * `JanelaListada` seria a armadilha de quem fosse escrever `setQueryData` com
 * ela — a lista passaria a ter um item cujo `_count.changes` é `undefined`, e o
 * combo de agendamento quebraria ao lê-lo. Quem chama invalida a listagem.
 */
export const criarJanela = (corpo: NovaJanela) =>
  post<JanelaDeMudanca>(`${CALENDARIO}/windows`, corpo);

export interface AlteracaoDeJanela {
  name?: string;
  description?: string;
  startsAt?: string;
  endsAt?: string;
  ownerTeamId?: string;
  recurrenceNote?: string;
  status?: SituacaoDeJanela;
  version?: number;
}

/** Linha crua, sem `_count` — ver a nota em `criarJanela`. */
export const alterarJanela = (windowId: string, corpo: AlteracaoDeJanela) =>
  patch<JanelaDeMudanca>(`${CALENDARIO}/windows/${windowId}`, corpo);

export const listarCongelamentos = () => get<Congelamento[]>(`${CALENDARIO}/blackouts`);

export interface NovoCongelamento {
  name: string;
  reason: string;
  startsAt: string;
  endsAt: string;
  enforcement?: Enforcement;
  scopeServiceIds?: string[];
  scopeCiIds?: string[];
  scopeOrganizationIds?: string[];
}

export const criarCongelamento = (corpo: NovoCongelamento) =>
  post<Congelamento>(`${CALENDARIO}/blackouts`, corpo);

export interface AlteracaoDeCongelamento {
  name?: string;
  reason?: string;
  startsAt?: string;
  endsAt?: string;
  enforcement?: Enforcement;
  scopeServiceIds?: string[];
  scopeCiIds?: string[];
  scopeOrganizationIds?: string[];
  active?: boolean;
  version?: number;
}

export const alterarCongelamento = (id: string, corpo: AlteracaoDeCongelamento) =>
  patch<Congelamento>(`${CALENDARIO}/blackouts/${id}`, corpo);

export const excluirJanela = (windowId: string) =>
  api<void>(`${CALENDARIO}/windows/${windowId}`, { method: 'DELETE' });

export const excluirCongelamento = (id: string) =>
  api<void>(`${CALENDARIO}/blackouts/${id}`, { method: 'DELETE' });

/** Instala os três fluxos do ITIL e as quatro políticas. Idempotente. */
export const provisionarFluxos = () =>
  post<Provisionamento>(`${CALENDARIO}/provision-workflows`);

// ── catálogo, para os seletores de escopo ──────────────────────────────────

export const listarServicos = () => get<ServicoDoCatalogo[]>('/catalog/services');

export const listarComponentes = (serviceId: string) =>
  get<ComponenteDeServico[]>(`/catalog/services/${serviceId}/components`);

// ── chaves do TanStack Query ───────────────────────────────────────────────

/**
 * Duas raízes, e não uma.
 *
 * `calendario` é do planejamento — janelas e congelamentos mudam em ritmo de
 * administração. A mudança de um chamado pendura em `chamados`, a mesma raiz
 * que o painel de transições invalida: agendar mexe na mudança e nos guards do
 * fluxo ao mesmo tempo, e as duas coisas precisam recarregar juntas.
 */
export const chavesDeMudanca = {
  raiz: ['mudancas'] as const,
  /**
   * O prefixo de **todos** os recortes do calendário.
   *
   * Existe para a invalidação ser cirúrgica: quem muda a Interrupção Projetada
   * de Serviço precisa recarregar o calendário, e não o catálogo de serviços,
   * os componentes de cada um deles e o questionário de risco — que é o que
   * invalidar a raiz faria, refazendo dezenas de consultas ativas na mesma
   * ficha e anulando o `staleTime` de cinco minutos que elas declaram.
   */
  calendarios: ['mudancas', 'calendario'] as const,
  calendario: (de: string, ate: string) => ['mudancas', 'calendario', de, ate] as const,
  janelas: ['mudancas', 'janelas'] as const,
  janela: (id: string) => ['mudancas', 'janela', id] as const,
  congelamentos: ['mudancas', 'congelamentos'] as const,
  questionario: ['mudancas', 'questionario'] as const,
  servicos: ['mudancas', 'servicos'] as const,
  componentes: (serviceId: string) => ['mudancas', 'componentes', serviceId] as const,
  /** Sob a raiz de chamados de propósito — ver a nota acima. */
  mudanca: (ticketId: string) => ['chamados', 'item', ticketId, 'mudanca'] as const,
  previa: (ticketId: string, windowId: string) =>
    ['chamados', 'item', ticketId, 'previa', windowId] as const,
};
